import React from 'react';
import { cn } from '../../../utils/cn';
import { inrCompact } from '../constants';
import { CockpitSummary } from '../types';

/**
 * Executive brief — "Altitude 1". Redesigned for scannability: the headline is a
 * row of promoted stat blocks (not a run-on sentence), followed by movement cards,
 * a labelled aging ribbon, and a compact forensic strip. Numbers are tabular and
 * weighted; colour is used only to signal meaning (red = leak, amber = risk).
 */

const AGING_ORDER = ['not_due', '0-30', '30-60', '60-90', '90+'] as const;
const AGING_BAR_CLASS: Record<(typeof AGING_ORDER)[number], string> = {
	not_due: 'bg-slate-300',
	'0-30': 'bg-emerald-400',
	'30-60': 'bg-amber-400',
	'60-90': 'bg-orange-400',
	'90+': 'bg-red-500',
};
const AGING_LBL: Record<(typeof AGING_ORDER)[number], string> = {
	not_due: 'Not due',
	'0-30': '0–30d',
	'30-60': '30–60d',
	'60-90': '60–90d',
	'90+': '90+ d',
};

const trimCustomer = (name: string) => name.replace(/ (PRIVATE|LIMITED|INDIA|SERVICES).*/i, '');

export const ExecBrief: React.FC<{ summary: CockpitSummary }> = ({ summary }) => {
	const b = summary.exec_brief;
	if (!b) return null;
	const ag = b.aging;
	const total = AGING_ORDER.reduce((t, k) => t + (ag[k]?.inr || 0), 0) || 1;
	const topc = (b.top_customers || [])
		.slice(0, 2)
		.map(c => trimCustomer(c.name))
		.join(' & ');
	const ai = summary.ai_brief;
	const genDate = new Date(summary.generated_at).toLocaleDateString('en-IN', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	});

	return (
		<section className='overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm'>
			{/* Eyebrow */}
			<div className='flex items-center justify-between border-b border-slate-100 px-6 py-3'>
				<span className='text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400'>
					Executive Brief
				</span>
				<span className='text-[11px] font-medium text-slate-400'>
					{genDate}
					{ai ? ' · AI-generated' : ''}
				</span>
			</div>

			{/* Headline stat row — the run-on sentence, now scannable */}
			{ai?.ceo_brief ? (
				<p className='px-6 pt-5 text-[15px] leading-relaxed text-slate-700'>{ai.ceo_brief}</p>
			) : (
				<div className='grid grid-cols-2 gap-px bg-slate-100 sm:grid-cols-4'>
					<HeadlineStat
						value={inrCompact(b.outstanding_inr)}
						label='Outstanding'
						sub={`across ${b.n_unpaid} invoices`}
					/>
					<HeadlineStat
						value={inrCompact(b.leak_inr)}
						label='Client says paid'
						sub='verify & reconcile'
						tone='leak'
					/>
					<HeadlineStat
						value={inrCompact(ag['90+'].inr)}
						label='Past 90 days'
						sub={`${ag['90+'].count} invoices`}
						tone='warn'
					/>
					<HeadlineStat
						value={topc.split(' & ')[0]}
						label='Top exposure'
						sub={topc.split(' & ')[1] ? `& ${topc.split(' & ')[1]}` : 'concentration'}
						small
					/>
				</div>
			)}

			{/* Movement cards */}
			<div className='grid grid-cols-1 gap-3 px-6 py-5 sm:grid-cols-3'>
				<MovementCard
					tone='collect'
					k='Collectable now'
					v={inrCompact(b.collectable_now_inr)}
					n='leak-to-reconcile + proven overdue'
				/>
				<MovementCard tone='risk' k='At risk' v={inrCompact(b.at_risk_inr)} n='90+ days & disputed' />
				<MovementCard
					tone='lead'
					k='Our move'
					v={String(b.outreach.our_move)}
					n='invoices ball-in-our-court'
				/>
			</div>

			{/* Aging ribbon */}
			<div className='px-6 pb-2'>
				<div className='flex h-2 overflow-hidden rounded-full'>
					{AGING_ORDER.map(k => (
						<div
							key={k}
							className={AGING_BAR_CLASS[k]}
							style={{ width: `${(100 * (ag[k]?.inr || 0)) / total}%` }}
							title={`${AGING_LBL[k]}: ${inrCompact(ag[k]?.inr || 0)} (${ag[k]?.count || 0})`}
						/>
					))}
				</div>
				<div className='mt-2.5 flex flex-wrap gap-x-5 gap-y-1.5'>
					{AGING_ORDER.map(k => (
						<span key={k} className='inline-flex items-center gap-1.5 text-[11px] text-slate-500'>
							<span className={cn('inline-block h-2 w-2 rounded-sm', AGING_BAR_CLASS[k])} />
							<span className='font-medium text-slate-600'>{AGING_LBL[k]}</span>
							<span className='tabular-nums'>{inrCompact(ag[k]?.inr || 0)}</span>
						</span>
					))}
				</div>
			</div>

			{/* Forensic strip */}
			{b.forensic && b.forensic.audited > 0 && (
				<div className='mx-6 my-4 flex flex-wrap items-center gap-x-5 gap-y-1 rounded-xl bg-slate-50 px-4 py-2.5 text-[12px] text-slate-600'>
					<span className='font-semibold text-slate-700'>◆ Deep review</span>
					<ForensicStat n={b.forensic.audited} label='read end-to-end' />
					<ForensicStat
						n={b.forensic.audit_disagreements}
						label='audit corrects dashboard'
						tone='leak'
					/>
					<ForensicStat n={b.forensic.with_high_tactics} label='aggressive pushback' tone='leak' />
					<ForensicStat n={b.forensic.hearsay_claims} label='unproven claims' />
					<ForensicStat n={b.forensic.misclassified} label='misclassified' />
				</div>
			)}

			{/* AI actions */}
			{ai?.actions && ai.actions.length > 0 && (
				<div className='space-y-2 px-6 pb-5'>
					<div className='text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400'>
						Do this — prioritized
					</div>
					{ai.actions.map((a, i) => (
						<div key={i} className='flex gap-3 rounded-xl border border-slate-100 p-3'>
							<span className='flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white'>
								{a.priority}
							</span>
							<div className='flex-1'>
								<div className='text-sm font-semibold text-slate-900'>
									{a.title}
									{a.impact_inr ? (
										<span className='ml-2 text-xs font-semibold text-emerald-600'>
											{inrCompact(a.impact_inr)}
										</span>
									) : null}
								</div>
								<div className='text-xs text-slate-500'>{a.detail}</div>
							</div>
						</div>
					))}
				</div>
			)}
		</section>
	);
};

const STAT_TONE = {
	default: 'text-slate-900',
	leak: 'text-red-600',
	warn: 'text-amber-600',
} as const;

const HeadlineStat: React.FC<{
	value: string;
	label: string;
	sub: string;
	tone?: keyof typeof STAT_TONE;
	small?: boolean;
}> = ({ value, label, sub, tone = 'default', small }) => (
	<div className='bg-white px-6 py-5'>
		<div className='text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400'>{label}</div>
		<div
			className={cn(
				'mt-1 font-extrabold tabular-nums leading-none',
				small ? 'truncate text-base' : 'text-2xl',
				STAT_TONE[tone]
			)}
			title={small ? value : undefined}
		>
			{value}
		</div>
		<div className='mt-1 text-[11px] text-slate-400'>{sub}</div>
	</div>
);

const MOVEMENT_TONE = {
	collect: { ring: 'border-emerald-200 bg-emerald-50/60', dot: 'bg-emerald-500' },
	risk: { ring: 'border-red-200 bg-red-50/60', dot: 'bg-red-500' },
	lead: { ring: 'border-sky-200 bg-sky-50/60', dot: 'bg-sky-500' },
} as const;

const MovementCard: React.FC<{
	tone: keyof typeof MOVEMENT_TONE;
	k: string;
	v: string;
	n: string;
}> = ({ tone, k, v, n }) => {
	const t = MOVEMENT_TONE[tone];
	return (
		<div className={cn('rounded-xl border p-4', t.ring)}>
			<div className='flex items-center gap-2'>
				<span className={cn('h-1.5 w-1.5 rounded-full', t.dot)} />
				<span className='text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500'>{k}</span>
			</div>
			<div className='mt-1.5 text-xl font-extrabold tabular-nums text-slate-900'>{v}</div>
			<div className='text-[11px] text-slate-500'>{n}</div>
		</div>
	);
};

const ForensicStat: React.FC<{ n: number; label: string; tone?: 'leak' }> = ({ n, label, tone }) => (
	<span className='inline-flex items-baseline gap-1'>
		<b className={cn('tabular-nums', tone === 'leak' ? 'text-red-600' : 'text-slate-800')}>{n}</b>
		<span className='text-slate-500'>{label}</span>
	</span>
);
